'use client';

import { useState, useEffect, useRef } from 'react';
import { RotateCcw, Award, Clock, Target, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { typingSpeedTestContent } from '../../data/typing-speed-test-content';

// Sample text for typing test
const sampleTexts = [
    "The quick brown fox jumps over the lazy dog. This sentence contains all the letters in the English alphabet.",
    "To be or not to be, that is the question. Whether 'tis nobler in the mind to suffer the slings and arrows of outrageous fortune.",
    "The journey of a thousand miles begins with a single step. Success is not final, failure is not fatal: It is the courage to continue that counts.",
    "In the middle of difficulty lies opportunity. The only way to do great work is to love what you do.",
    "Life is what happens when you're busy making other plans. The future belongs to those who believe in the beauty of their dreams.",
];

const TypingSpeedTest = () => {
    const [text, setText] = useState('');
    const [input, setInput] = useState('');
    const [timeLeft, setTimeLeft] = useState(60);
    const [isTyping, setIsTyping] = useState(false);
    const [wpm, setWpm] = useState(0);
    const [accuracy, setAccuracy] = useState(100);
    const [correctChars, setCorrectChars] = useState(0);
    const [totalTyped, setTotalTyped] = useState(0);
    const [testComplete, setTestComplete] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [showCountdown, setShowCountdown] = useState(false);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownRef = useRef<NodeJS.Timeout | null>(null);

    // Get a random sample text
    const getRandomText = (): string => {
        return sampleTexts[Math.floor(Math.random() * sampleTexts.length)];
    };

    // Start a new test
    const startNewTest = () => {
        setText(getRandomText());
        setInput('');
        setTimeLeft(60);
        setWpm(0);
        setAccuracy(100);
        setCorrectChars(0);
        setTotalTyped(0);
        setTestComplete(false);
        setShowResults(false);
        setShowCountdown(true);
        setCountdown(3);

        // Start countdown
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    if (countdownRef.current) {
                        clearInterval(countdownRef.current);
                        countdownRef.current = null;
                    }
                    setShowCountdown(false);
                    startTypingTest();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Start the typing test
    const startTypingTest = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        setIsTyping(true);
        setTimeLeft(60);

        // Start timer
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    endTest();
                    return 0;
                }
                return prev - 1;
            });

            // Calculate WPM (words per minute)
            const words = input.trim() === '' ? 0 : input.trim().split(/\s+/).length;
            const timeElapsed = 60 - timeLeft + 1;
            const currentWpm = Math.round((words / timeElapsed) * 60);
            setWpm(currentWpm);

        }, 1000);

        // Focus the input
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // End the test
    const endTest = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setIsTyping(false);
        setTestComplete(true);
        setShowResults(true);

        // Calculate final WPM and accuracy
        const words = input.trim() === '' ? 0 : input.trim().split(/\s+/).length;
        const finalWpm = Math.round((words / 60) * 60);
        setWpm(finalWpm);

        const acc = totalTyped > 0 ? Math.round((correctChars / totalTyped) * 100) : 0;
        setAccuracy(acc);
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!isTyping || testComplete) return;

        const value = e.target.value;
        setInput(value);

        // Calculate accuracy
        let correct = 0;
        for (let i = 0; i < value.length; i++) {
            if (i < text.length && value[i] === text[i]) {
                correct++;
            }
        }

        setCorrectChars(correct);
        setTotalTyped(value.length);

        // Update accuracy
        const acc = value.length > 0 ? Math.round((correct / value.length) * 100) : 100;
        setAccuracy(acc);

        // Check if all text is typed correctly
        if (value === text) {
            // Calculate final WPM before ending test
            const words = value.trim() === '' ? 0 : value.trim().split(/\s+/).length;
            const finalWpm = Math.round((words / (60 - timeLeft)) * 60);
            setWpm(finalWpm);

            // End the test with a small delay to ensure state updates
            setTimeout(() => {
                endTest();
                toast.success('Perfect! You completed the text!');
            }, 0);
        }
    };

    // Get the current character's class
    const getCharClass = (char: string, idx: number) => {
        if (idx >= input.length) return 'text-gray-400';
        return char === input[idx] ? 'text-green-500' : 'text-red-500';
    };

    // Initialize with a random text
    useEffect(() => {
        setText(getRandomText());
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
        };
    }, []);

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <ToolPageIntro title="Typing Speed Test - Free Online WPM Test & Typing Practice">
                Test your typing speed and accuracy with our free online typing test. Measure WPM, improve your skills, and track your progress.
            </ToolPageIntro>
            </div>

            {/* Test Controls */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center text-gray-700">
                        <Clock className="h-5 w-5 mr-1" />
                        <span>{timeLeft}s</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                        <Target className="h-5 w-5 mr-1" />
                        <span>{wpm} WPM</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                        {accuracy >= 90 ? (
                            <CheckCircle className="h-5 w-5 mr-1 text-green-500" />
                        ) : (
                            <XCircle className="h-5 w-5 mr-1 text-red-500" />
                        )}
                        <span>{accuracy}%</span>
                    </div>
                </div>

                <button
                    onClick={startNewTest}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    New Test
                </button>
            </div>

            {/* Countdown */}
            {showCountdown && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-xl text-center">
                        <div className="text-6xl font-bold text-blue-600 mb-4">{countdown}</div>
                        <p className="text-gray-700">Get ready to type!</p>
                    </div>
                </div>
            )}

            {/* Typing Area */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                <div
                    className="text-lg leading-relaxed mb-6 p-4 bg-gray-50 rounded border border-gray-200 min-h-32 cursor-text"
                    onClick={() => inputRef.current?.focus()}
                >
                    {text.split('').map((char, idx) => (
                        <span
                            key={idx}
                            className={`${getCharClass(char, idx)} ${idx === input.length && isTyping ? 'bg-blue-100' : ''}`}
                        >
                            {char}
                        </span>
                    ))}
                </div>

                <textarea
                    ref={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    disabled={!isTyping || testComplete}
                    placeholder={isTyping ? 'Start typing here...' : 'Click "New Test" to begin'}
                    className="w-full p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none min-h-32"
                />

                <div className="mt-4 text-sm text-gray-500">
                    <p>Tip: Try to type without looking at your keyboard for better results.</p>
                </div>
            </div>

            {/* Results */}
            {showResults && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                        <Award className="h-6 w-6 mr-2 text-yellow-500" />
                        Test Results
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-blue-50 p-6 rounded-lg text-center">
                            <div className="text-4xl font-bold text-blue-600 mb-2">{wpm}</div>
                            <div className="text-sm font-medium text-gray-600">Words Per Minute</div>
                            <div className="mt-2 text-xs text-gray-500">
                                {wpm < 20 ? 'Beginner' : wpm < 40 ? 'Intermediate' : wpm < 60 ? 'Advanced' : 'Expert'}
                            </div>
                        </div>

                        <div className="bg-green-50 p-6 rounded-lg text-center">
                            <div className="text-4xl font-bold text-green-600 mb-2">{accuracy}%</div>
                            <div className="text-sm font-medium text-gray-600">Accuracy</div>
                            <div className="mt-2 text-xs text-gray-500">
                                {accuracy < 70 ? 'Needs Improvement' : accuracy < 90 ? 'Good' : 'Excellent'}
                            </div>
                        </div>

                        <div className="bg-purple-50 p-6 rounded-lg text-center">
                            <div className="text-4xl font-bold text-purple-600 mb-2">
                                {Math.round((correctChars / 5) / (60 - timeLeft) * 60) || 0}
                            </div>
                            <div className="text-sm font-medium text-gray-600">Adjusted WPM</div>
                            <div className="mt-2 text-xs text-gray-500">
                                (WPM × accuracy %)
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Your Performance</h3>
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Correct Characters:</span>
                                <span className="font-medium">{correctChars}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Incorrect Characters:</span>
                                <span className="font-medium">{totalTyped - correctChars}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Total Characters:</span>
                                <span className="font-medium">{totalTyped}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Time Taken:</span>
                                <span className="font-medium">{60 - timeLeft} seconds</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Tips to Improve</h3>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                <span>Practice regularly to improve your typing speed and accuracy.</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                <span>Try to type without looking at your keyboard.</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                <span>Maintain good posture and hand position.</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">•</span>
                                <span>Take breaks to avoid strain during long typing sessions.</span>
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            {/* How It Works */}
            <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h2>
                <div className="space-y-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full mr-3">
                            <Clock className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">60-Second Test</h3>
                            <p className="text-gray-600 text-sm">
                                Type as much as you can in 60 seconds. The test will automatically end when time's up.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-green-100 p-2 rounded-full mr-3">
                            <Target className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">Words Per Minute (WPM)</h3>
                            <p className="text-gray-600 text-sm">
                                Your typing speed is calculated based on the number of words you type per minute.
                                A word is standardized to 5 characters including spaces and punctuation.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full mr-3">
                            <CheckCircle className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900">Accuracy</h3>
                            <p className="text-gray-600 text-sm">
                                Your accuracy is calculated based on the number of correct characters you type.
                                Try to maintain high accuracy while increasing your speed.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    {/* Informational Content */ }
    <div className="mt-12">
        <InformationalContent
            title={typingSpeedTestContent.title}
            description={typingSpeedTestContent.description}
            content={typingSpeedTestContent.content}
            faqs={typingSpeedTestContent.faqs}
            relatedTools={typingSpeedTestContent.relatedTools}
        />
    </div>
};

export default TypingSpeedTest;
